import { useState } from 'react'
import { useI18n } from '../stores/I18nContext'
import { downloadUrl } from '../utils/download'
import './HelpModal.css'

const SKILL_URL = '/skills/time-distance-plan.skill'

// Save the skill as a Blob so a PWA service worker can't return index.html.
function handleSkillDownload(e) {
  e.preventDefault()
  downloadUrl(SKILL_URL, 'time-distance-plan.skill').catch(() => {
    window.location.href = SKILL_URL
  })
}

export default function HelpModal({ isOpen, onClose }) {
  const { language } = useI18n()
  const [activeTab, setActiveTab] = useState('concept')
  const isNorwegian = language === 'nb'

  if (!isOpen) return null

  const tabs = isNorwegian
    ? [
        { id: 'concept', label: 'Konsept' },
        { id: 'teddo', label: 'TeDdO' },
        { id: 'howto', label: 'Slik bruker du' },
        { id: 'shortcuts', label: 'Hurtigtaster' }
      ]
    : [
        { id: 'concept', label: 'Concept' },
        { id: 'teddo', label: 'TeDdO' },
        { id: 'howto', label: 'How to Use' },
        { id: 'shortcuts', label: 'Shortcuts' }
      ]

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Time-Distance Organizer</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`modal-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="modal-body">
          {activeTab === 'concept' && (
            <ConceptTab isNorwegian={isNorwegian} />
          )}
          {activeTab === 'teddo' && (
            <TeddoTab isNorwegian={isNorwegian} />
          )}
          {activeTab === 'howto' && (
            <HowToTab isNorwegian={isNorwegian} />
          )}
          {activeTab === 'shortcuts' && (
            <ShortcutsTab isNorwegian={isNorwegian} />
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>
            {isNorwegian ? 'Lukk' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ConceptTab({ isNorwegian }) {
  if (isNorwegian) {
    return (
      <>
        <div className="help-section">
          <h4>Hva er Time-Distance Organizer?</h4>
          <p>Time-Distance Organizer (TDO) er et visuelt planleggingsverktøy inspirert av japanske togdiagrammer. I stedet for tradisjonelle Gantt-diagrammer, viser TDO prosjektet ditt som <strong>bevegelser gjennom tid og rom</strong>.</p>
        </div>
        <div className="help-section">
          <h4>Hvorfor TDO fremfor andre metoder?</h4>
          <p>Tradisjonelle planleggingsverktøy fokuserer på <em>"Hvem gjør hva, og når?"</em>. TDO snur perspektivet:</p>
          <p className="quote">"Hvordan beveger arbeidet seg gjennom systemet over tid?"</p>
          <ul>
            <li><strong>Gantt-diagrammer:</strong> Viser oppgaver som horisontale barer. Bra for ressursplanlegging, men vanskelig å se flyt og avhengigheter.</li>
            <li><strong>Kanban:</strong> Viser status (To Do, Doing, Done), men mangler tidsdimensjon og parallelle arbeidsstrømmer.</li>
            <li><strong>TDO:</strong> Viser <em>bevegelse</em> - du ser hvordan flere arbeidsstrømmer krysser hverandre, hvor flaskehalser oppstår, og rytmen i prosjektet.</li>
          </ul>
        </div>
        <div className="help-section">
          <h4>Kjernekonsepter</h4>
          <ul>
            <li><strong>X-aksen (Tid):</strong> Tid eller sekvens - uker, faser, steg, sprints</li>
            <li><strong>Y-aksen (Avstand):</strong> Prosessfaser, beslutningspunkter, eller "avstand til ferdig verdi"</li>
            <li><strong>Flyter:</strong> Arbeidsstrømmer eller delprosjekter (fargede linjer som beveger seg gjennom diagrammet)</li>
            <li><strong>Checkpoints:</strong> Milepæler og oppgaver langs en flyt</li>
            <li><strong>Avhengigheter:</strong> Koblinger mellom checkpoints som viser hva som må være ferdig først</li>
          </ul>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="help-section">
        <h4>What is Time-Distance Organizer?</h4>
        <p>Time-Distance Organizer (TDO) is a visual planning tool inspired by Japanese train diagrams. Instead of traditional Gantt charts, TDO shows your project as <strong>movements through time and space</strong>.</p>
      </div>
      <div className="help-section">
        <h4>Why TDO over other methods?</h4>
        <p>Traditional planning tools focus on <em>"Who does what, and when?"</em>. TDO flips the perspective:</p>
        <p className="quote">"How does work move through the system over time?"</p>
        <ul>
          <li><strong>Gantt charts:</strong> Show tasks as horizontal bars. Good for resource planning, but hard to see flow and dependencies.</li>
          <li><strong>Kanban:</strong> Shows status (To Do, Doing, Done), but lacks time dimension and parallel workflows.</li>
          <li><strong>TDO:</strong> Shows <em>movement</em> - you see how multiple workflows intersect, where bottlenecks occur, and the rhythm of your project.</li>
        </ul>
      </div>
      <div className="help-section">
        <h4>Core Concepts</h4>
        <ul>
          <li><strong>X-axis (Time):</strong> Time or sequence - weeks, phases, steps, sprints</li>
          <li><strong>Y-axis (Distance):</strong> Process phases, decision points, or "distance to delivered value"</li>
          <li><strong>Flows:</strong> Workflows or sub-projects (colored lines moving through the diagram)</li>
          <li><strong>Checkpoints:</strong> Milestones and tasks along a flow</li>
          <li><strong>Dependencies:</strong> Links between checkpoints showing what must be done first</li>
        </ul>
      </div>
    </>
  )
}

function TeddoTab({ isNorwegian }) {
  if (isNorwegian) {
    return (
      <div className="teddo-tab-layout">
        <div className="teddo-character-container">
          <img src="/teddo_the_character.png" alt="TeDdO" className="teddo-character-full" />
        </div>
        <div className="teddo-content">
          <div className="help-section">
            <h4>Møt TeDdO</h4>
            <p>TeDdO er din AI-assistent for prosjektplanlegging. Navnet kommer fra <strong>T</strong>ime-<strong>D</strong>istance <strong>O</strong>rganizer (de store bokstavene: T-D-O). Karakteren er en vennlig og hjelpsom figur inspirert av Studio Ghibli-estetikk.</p>
          </div>
          <div className="help-section">
            <h4>TeDdOs personlighet</h4>
            <ul>
              <li>🌱 <strong>Tålmodig og oppmuntrende:</strong> Som Totoro - rolig, støttende</li>
              <li>✨ <strong>Magisk hjelpsom:</strong> Foreslår optimaliseringer og identifiserer konflikter</li>
              <li>🎨 <strong>Visuelt orientert:</strong> Elsker diagrammer og flows</li>
              <li>🚂 <strong>Togentusiast:</strong> Presisjon møter eleganse</li>
            </ul>
          </div>
          <div className="help-section">
            <h4>TeDdO Copilot (kommer snart)</h4>
            <ul>
              <li>💡 Foreslå optimale rekkefølger</li>
              <li>⚡ Identifisere flaskehalser</li>
              <li>📊 Generere innsiktsrapporter</li>
              <li>💬 Svare på spørsmål om prosjektet</li>
            </ul>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="teddo-tab-layout">
      <div className="teddo-character-container">
        <img src="/teddo_the_character.png" alt="TeDdO" className="teddo-character-full" />
      </div>
      <div className="teddo-content">
        <div className="help-section">
          <h4>Meet TeDdO</h4>
          <p>TeDdO is your AI assistant for project planning. The name comes from <strong>T</strong>ime-<strong>D</strong>istance <strong>O</strong>rganizer (the capital letters: T-D-O). The character is a friendly and helpful figure inspired by Studio Ghibli aesthetics.</p>
        </div>
        <div className="help-section">
          <h4>TeDdO's Personality</h4>
          <ul>
            <li>🌱 <strong>Patient and encouraging:</strong> Like Totoro - calm, supportive</li>
            <li>✨ <strong>Magically helpful:</strong> Suggests optimizations and identifies conflicts</li>
            <li>🎨 <strong>Visually oriented:</strong> Loves diagrams and flows</li>
            <li>🚂 <strong>Train enthusiast:</strong> Precision meets elegance</li>
          </ul>
        </div>
        <div className="help-section">
          <h4>TeDdO Copilot (coming soon)</h4>
          <ul>
            <li>💡 Suggest optimal task sequences</li>
            <li>⚡ Identify bottlenecks</li>
            <li>📊 Generate insight reports</li>
            <li>💬 Answer questions about your project</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

function HowToTab({ isNorwegian }) {
  if (isNorwegian) {
    return (
      <>
        <div className="help-section">
          <h4>Kom i gang</h4>
          <ul>
            <li>Klikk <strong>"Legg til flyt"</strong> for å opprette en arbeidsstrøm</li>
            <li>Velg en flow i sidepanelet, deretter <strong>klikk på canvas</strong> for å legge til checkpoints</li>
            <li><strong>Dra checkpoints</strong> for å flytte dem til nye posisjoner</li>
            <li>Klikk på en checkpoint for å redigere navn, status og type</li>
          </ul>
        </div>
        <div className="help-section">
          <h4>Tilpasse aksene</h4>
          <ul>
            <li>Åpne <strong>Innstillinger</strong> (tannhjulet)</li>
            <li>Gå til <strong>Akser</strong>-fanen</li>
            <li>Velg X-akse-modus (Uker, Faser, Steg, osv.)</li>
            <li>Definer Y-akse-etiketter (en per linje)</li>
          </ul>
        </div>
        <div className="help-section">
          <h4>Lagre og dele</h4>
          <ul>
            <li>Prosjektet lagres automatisk i nettleseren</li>
            <li>Bruk <strong>Fil → Eksporter JSON</strong> for å laste ned</li>
            <li>Del JSON-filen med andre</li>
          </ul>
        </div>
        <div className="help-section">
          <h4>Bygg plan med en annen AI</h4>
          <p>La et annet AI-verktøy (f.eks. Claude eller ChatGPT) lage en hel Time-Distance-plan for deg og importer den til Teddo:</p>
          <ol>
            <li>Last ned <a href={SKILL_URL} download="time-distance-plan.skill" onClick={handleSkillDownload}>Teddo plan-ferdigheten (.skill)</a> og gi den til AI-verktøyet ditt (eller lim inn innholdet).</li>
            <li>Be AI-en lage en plan fra dokumentet, briefen eller oppgavelisten din — den lager en <code>.json</code>-fil.</li>
            <li>I Teddo: åpne <strong>Teddo</strong>-fanen, trykk 📎 og legg ved <code>.json</code>-filen, og skriv «Bygg denne planen».</li>
            <li>Teddo stiller et par spørsmål, viser planen, og du trykker <strong>Accept</strong> én gang.</li>
          </ol>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="help-section">
        <h4>Getting Started</h4>
        <ul>
          <li>Click <strong>"Add Flow"</strong> to create a workflow</li>
          <li>Select a flow in the sidebar, then <strong>click on the canvas</strong> to add checkpoints</li>
          <li><strong>Drag checkpoints</strong> to move them to new positions</li>
          <li>Click a checkpoint to edit its name, status, and type</li>
        </ul>
      </div>
      <div className="help-section">
        <h4>Customizing Axes</h4>
        <ul>
          <li>Open <strong>Settings</strong> (gear icon)</li>
          <li>Go to the <strong>Axes</strong> tab</li>
          <li>Choose X-axis mode (Weeks, Phases, Steps, etc.)</li>
          <li>Define Y-axis labels (one per line)</li>
        </ul>
      </div>
      <div className="help-section">
        <h4>Saving & Sharing</h4>
        <ul>
          <li>Your project auto-saves in the browser</li>
          <li>Use <strong>File → Export JSON</strong> to download</li>
          <li>Share the JSON file with others</li>
        </ul>
      </div>
      <div className="help-section">
        <h4>Build a plan with another AI</h4>
        <p>Let another AI tool (e.g. Claude or ChatGPT) draft a whole Time-Distance plan and import it into Teddo:</p>
        <ol>
          <li>Download the <a href={SKILL_URL} download="time-distance-plan.skill" onClick={handleSkillDownload}>Teddo plan skill (.skill)</a> and give it to your AI tool (or paste its contents).</li>
          <li>Ask the AI to build a plan from your document, brief, or task list — it produces a <code>.json</code> file.</li>
          <li>In Teddo: open the <strong>Teddo</strong> tab, tap 📎 and attach the <code>.json</code>, then type "Build this plan".</li>
          <li>Teddo asks a couple of questions, shows the plan, and you tap <strong>Accept</strong> once.</li>
        </ol>
      </div>
    </>
  )
}

function ShortcutsTab({ isNorwegian }) {
  const shortcuts = isNorwegian
    ? [
        { action: 'Lagre prosjekt', key: 'Ctrl+S' },
        { action: 'Eksporter JSON', key: 'Ctrl+E' },
        { action: 'Lukk dialoger', key: 'Esc' }
      ]
    : [
        { action: 'Save project', key: 'Ctrl+S' },
        { action: 'Export JSON', key: 'Ctrl+E' },
        { action: 'Close dialogs', key: 'Esc' }
      ]

  return (
    <div className="shortcuts-list">
      {shortcuts.map((shortcut, i) => (
        <div key={i} className="keyboard-shortcut">
          <span>{shortcut.action}</span>
          <kbd>{shortcut.key}</kbd>
        </div>
      ))}
    </div>
  )
}
